<template>
  <v-main class="py-4 px-3">
    <template v-if="quota_free > 0">
      <a
        v-if="dropzone"
        class="dropzone d-flex justify-center align-center pa-4 grey--text"
        :class="{'flex-column': !dense}"
        @click="$refs.files.$refs.input.click()"
      >
        <v-icon
          :class="{
            'mr-2': dense,
            'mb-2': !dense
          }"
          :x-large="!dense"
        >far fa-file-upload</v-icon>
        <span>Dateien hochladen</span>
      </a>

      <div v-show="!dropzone">
        <v-progress-linear
          v-model="progress"
          :active="loading"
          :indeterminate="query"
          :query="true"
        ></v-progress-linear>

        <v-alert v-if="error" type="error" class="mb-4" text>
          <template v-if="errorCode === 'E_ROOM_QUOTA_EXCEEDED'">
            Speicherplatz von {{_size(room.quota)}} wurde überschritten.<br>
            Upgrade deinen <a :href="accountUrl" target="_blank">Tarif</a>
            für mehr Speicherplatz.
          </template>
          <template v-else>
            Dateien konnten nicht hochgeladen werden.
          </template>
        </v-alert>

        <v-form ref="form" :readonly="loading">
          <v-file-input
            ref="files"
            v-model="files"
            label="Dateien"
            placeholder="Dateien auswählen"
            clear-icon="fal fa-times"
            prepend-icon=""
            prepend-inner-icon="far fa-file-upload"
            multiple
            chips
            counter
            show-size
            outlined
          ></v-file-input>

          <v-text-field
            v-model="description"
            name="description"
            class="mb-6"
            label="Beschreibung (optional)"
            placeholder="Beschreibung eingeben"
            maxlength="255"
            outlined
            hide-details
            @keydown.enter="_submit"
          ></v-text-field>

          <PersistentInputLabel :focused="participantsFocused" class="mb-4">
            Teilen mit
            <template #input>
              <v-select
                id="files-participants"
                v-model="participants"
                :items="room.participants"
                item-value="id"
                item-text="name"
                no-data-text="Keine Benutzer vorhanden"
                placeholder="Allen Benutzern im Raum"
                persistent-placeholder
                append-icon="far fa-angle-down"
                multiple
                hide-details
                chips
                counter
                outlined
                @focus="participantsFocused = true"
                @blur="participantsFocused = false"
              >
                <template #prepend-item>
                  <v-list-item
                    :class="allParticipants ? 'primary--text v-list-item--active' : ''"
                    ripple
                    @click="_toggleAllParticipants"
                  >
                    <v-list-item-action>
                      <v-icon>{{selectIcon}}</v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Allen Benutzern im Raum</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-select>
            </template>
          </PersistentInputLabel>

          <div v-if="files.length > 1" class="d-flex mb-4">
            <div class="flex">
              Dateien zusammenfassen
              <div>
                <small v-if="zip">
                  Dateien werden in eine ZIP-Datei gespeichert.
                </small>
                <small v-if="!zip">
                  Dateien werden einzeln gespeichert.
                </small>
              </div>
            </div>
            <v-switch
              v-model="zip"
              name="zip"
              value="true"
              color="primary"
              class="pt-0 mt-0"
            ></v-switch>
          </div>
        </v-form>

        <div class="text-right">
          <v-btn
            :loading="loading"
            :disabled="!files.length"
            color="accent"
            depressed
            rounded
            large
            @click="_submit"
          >Dateien hochladen</v-btn>
        </div>
      </div>
    </template>

    <v-alert v-else type="warning" text class="mb-0">
      Du hast den Speicherplatz von {{_size(room.quota)}} verbraucht.<br>
      Upgrade deinen <a :href="accountUrl" target="_blank">Tarif</a>
      für mehr Speicherplatz.
    </v-alert>
  </v-main>
</template>

<script>
  import PersistentInputLabel from './PersistentInputLabel';
  import {compareAlphabetically, focusField, formatFileSize, accountUrl} from '../../assets/js/utils';

  export default {
    name: 'FilesUpload',

    components: {
      PersistentInputLabel
    },

    props: {
      dense: {
        type: Boolean,
        default: false
      },
      quotaFree: {
        type: Number,
        default: 0
      }
    },

    data() {
      return {
        loading: false,
        error: false,
        files: [],
        description: '',
        participants: [],
        zip: true,
        progress: 0,
        query: false,
        participantsFocused: false,
        accountUrl
      };
    },

    computed: {
      dropzone() {
        return !this.files?.length;
      },

      room() {
        const room = {...this.$store.getters['share/getRoom'] || {}};
        if (room.id !== 'local' && room.participants) {
          // Add owner to participants
          room.participants.push(room.owner);
          // Remove current user from participants.
          room.participants = room.participants.filter((participant) => {
            return !participant.isCurrentUser;
          });

          room.participants.sort((a, b) => compareAlphabetically(a.name, b.name));
        }
        return room;
      },

      quota_free() {
        return this.quotaFree;
      },

      allParticipants() {
        return this.participants.length === 0;
      },

      selectIcon() {
        if (this.allParticipants) {
          return 'fas fa-check-square';
        }
        return 'far fa-square';
      },

      errorCode() {
        return this.error?.response?.data?.error?.innererror?.code;
      }
    },

    watch: {
      files(files, prevFiles, zip = this.zip, prevZip = this.zip) {
        if (this._description(prevFiles, prevZip) === this.description) {
          this.description = this._description(files, zip);
        }
      },

      zip(zip, prevZip) {
        this.$options.watch.files.call(this, this.files, this.files, zip, prevZip);
      },

      progress(progress) {
        if (progress === 100) {
          this._queryTimeout = setTimeout(() => this.query = true, 1500);
        } else {
          clearInterval(this._queryTimeout);
          this.query = false;
        }
      }
    },

    methods: {
      focus() {
        const input = focusField(this.$refs.files);
        return input;
      },

      _toggleAllParticipants() {
        if (!this.allParticipants) {
          this.participants = [];
        }
      },

      async _submit() {
        if (this.loading || !this.files.length) {
          return;
        }

        this.error = false;
        this.loading = true;

        const formData = new FormData(this.$refs.form.$el);
        formData.set('roomId', this.room.id);
        for (const file of this.files) {
          formData.append('files', file);
        }
        formData.set('participants', this.participants.join(','));

        try {
          this.progress = 0;
          await this.$api.post('FilesUpload', formData, {
            onUploadProgress: (e) => this.progress = e.loaded / e.total * 100
          });
          this._resetForm();

        } catch (error) {
          this.error = error;
        }
        this.loading = false;
      },

      _resetForm() {
        const initialData = this.$options.data();

        this.$refs.form.reset();
        this.participants = initialData.participants;
        this.description = initialData.description;
        this.files = initialData.files;
      },

      _description(files, zip) {
        if (files.length > 1 && zip) {
          return files.map((f) => f.name).join(', ');
        }
        return '';
      },

      _size(size) {
        return formatFileSize(size);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .dropzone {
    height: 100%;
    border: 2px dashed lightgray;
    border-radius: 8px;
  }

  .v-progress-linear {
    position: absolute;
    top: -16px;
    left: -12px;
    width: calc(100% + 32px);
  }

  .v-select::v-deep .v-select__selections {
    min-height: 42px !important;
  }
</style>
